import {i18n} from "./i18n";
import * as mdc from "material-components-web";
import {select} from "material-components-web";
import UrlHelpers from "./url";
import {tenant_updated} from "./dapiEventUris";

export class TenantOverwrites {
    private readonly contentTypeHeaderValue = 'application/hal+json'
    private readonly tenantDataId = 'tenantData';
    private readonly appListId = 'subscribedAppsList';
    private readonly appOvListId = 'overwriteList';
    private readonly errorMessageId = 'errorMessage';

    private readonly _i18n: i18n;
    private readonly _dapi: Dapi;

    private readonly tenantUrl: string;
    private readonly appsUrl:string;

    private _mdcTextFields: { [id: string]: mdc.textField.MDCTextField } = {};
    private _mdcTextFieldHelpers: { [id: string]: mdc.textField.MDCTextFieldHelperText } = {};
    private _mdcSelectHelperTexts: { [id: string]: mdc.select.MDCSelectHelperText } = {};
    private _mdcLists: { [id: string]: mdc.list.MDCList } = {};
    private _mdcSelects: { [id: string]: mdc.select.MDCSelect } = {};
    private _mdcMenus: { [id: string]: mdc.menu.MDCMenu } = {};
    private _mdcSnackbar : { [id: string]: mdc.snackbar.MDCSnackbar } = {};
    private _mdcProgressBars: { [id: string]: mdc.linearProgress.MDCLinearProgress } = {};

    private selectObserver: MutationObserver;

    constructor() {
        this._dapi = (<any>window).dapi;
        this._i18n = new i18n();

        this.tenantUrl = UrlHelpers.getLinkRelHref('tenant');
        this.appsUrl = UrlHelpers.getLinkRelHref('apps');

        this.loadMaterial();

        this.closeProgress();

        this.setupDataObserver();
        this.setupSelectObserver();

        const btnAddOverwrite = <HTMLButtonElement>document.getElementById('btnAddOverwrite');
        if (btnAddOverwrite) {
           btnAddOverwrite.addEventListener('click', this.handleBtnAddClicked);
        }
    }

    private loadMaterial = () => {
        const selects = document.querySelectorAll('.mdc-select');
        Array.apply(null, selects).forEach((uList) => {
            const mdcSelect = mdc.select.MDCSelect.attachTo(uList);
            mdcSelect.layoutOptions();
            if (uList.id === 'appList_select') {
                mdcSelect.listen('MDCSelect:change', this.handleSelectApp);
            }
            if (uList.id) {
                this._mdcSelects[uList.id] = mdcSelect;
            }
        });

        const textFields = document.querySelectorAll('.mdc-text-field');
        Array.apply(null,textFields).forEach((textfield) => {
            const mdcTextfield = mdc.textField.MDCTextField.attachTo(textfield);
            if(!textfield.id) {
                const inputs = textfield.getElementsByTagName('input');
                if (inputs.length > 0 && inputs[0].id) {
                    this._mdcTextFields[inputs[0].id] = mdcTextfield;
                    return
                }
            }
            this._mdcTextFields[textfield.id] = mdcTextfield;
        });

        const textFieldHelpers = document.querySelectorAll('.mdc-text-field-helper-text');
        Array.apply(null,textFieldHelpers).forEach((helper) => {
            const mdcHelper = mdc.textField.MDCTextFieldHelperText.attachTo(helper);
            if(!helper.id) {
                this._mdcTextFieldHelpers[helper.id] = mdcHelper;
            }
        });

        const selectHelperTexts = document.querySelectorAll('.mdc-select-helper-text');
        Array.apply(null, selectHelperTexts).forEach((s: HTMLElement) => {
            const mdcSelectHelperText = select.MDCSelectHelperText.attachTo(s);
            if (s.id) {
                this._mdcSelectHelperTexts[s.id] = mdcSelectHelperText;
            }
        });

        const lists = document.querySelectorAll('.mdc-list');
        Array.apply(null,lists).forEach((list) => {
            const mdcList = mdc.list.MDCList.attachTo(list);
            if (list.id) {
                this._mdcLists[list.id] = mdcList;
            }
        });

        const menus = document.querySelectorAll('.mdc-menu');
        Array.apply(null, menus).forEach((m: HTMLElement) => {
            const mdcMenu = mdc.menu.MDCMenu.attachTo(m);
            if (m.id) {
                this._mdcMenus[m.id] = mdcMenu;
            }
        });

        const buttons = document.querySelectorAll('.mdc-button');
        Array.apply(null,buttons).forEach((btn) => {
            mdc.ripple.MDCRipple.attachTo(btn);
        });

        const snackbars = document.querySelectorAll('.mdc-snackbar');
        Array.apply(null, snackbars).forEach((sb) => {
            const mdcSn = mdc.snackbar.MDCSnackbar.attachTo(sb);
            if (sb.id) {
                this._mdcSnackbar[sb.id] = mdcSn;
            }
        });

        const progressBars = document.querySelectorAll('.mdc-linear-progress');
        Array.apply(null, progressBars).forEach((pBar: HTMLElement) => {
            const mdcBar = mdc.linearProgress.MDCLinearProgress.attachTo(pBar);
            if (pBar.id) {
                this._mdcProgressBars[pBar.id] = mdcBar;
            }
        });
    };

    private handleSelectApp = (e: any) => {
        e.target.dataset.selectedapp = e.detail.value;
    }

    private setupDataObserver = (): void => {
        const dataElement = document.getElementById(this.tenantDataId);

        const observedAttributeName = 'data-tenant';
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type == "attributes" && mutation.attributeName == observedAttributeName) {
                    this.renderAppList();
                    this.renderAppOverwriteList();
                }
            });
        });
        observer.observe(dataElement, {
            attributeFilter: [observedAttributeName]
        });

        this.renderAppList();
        this.renderAppOverwriteList();
    };

    private setupSelectObserver = () => {
        this.renderOverwriteValueTextfield('');
        const selectElement = document.getElementById('appList_select');
        const observedAttributeName = 'data-selectedapp';
        this.selectObserver = new MutationObserver(mutations => {
            mutations.forEach(value => {
                if (value.type == "attributes" && value.attributeName == observedAttributeName) {
                    const appName = selectElement.dataset.selectedapp;
                    this.renderOverwriteValueTextfield(appName);
                    this.disableBtnAdd(appName.isNullOrEmpty());
                }
            });
        });
        this.selectObserver.observe(selectElement, {
            attributes: true,
            attributeFilter: [observedAttributeName]
        });
    };

    private renderOverwriteValueTextfield = (appName: string) => {
        const hideClass = 'hide-element';

        const elementId = 'overwriteEndpoint_wrapper';
        const element = document.getElementById(elementId);
        const field: mdc.textField.MDCTextField = this._mdcTextFields[elementId];

        if (appName.isNullOrEmpty()) {
            if (!element.classList.contains(hideClass)) {
                element.classList.add(hideClass);
            }
            field.suffixText = '';
            return;
        }

        field.suffixText = '/' + appName;

        element.classList.remove(hideClass);

        field.focus();
    };

    private renderAppList = () => {
        const data = this.readDataFromElement();
        if (!data) {
            console.log('renderAppList: data missing');
            return
        }

        //this.renderEmptyTenantList();

        if (!data || data.subscribedApps.length === 0) {
            console.log('renderAppList: app list empty');
            return
        }

        const ul = document.getElementById(this.appListId);
        if(!ul) {
            console.log(`renderAppList: cannot find ul element with id '${this.appListId}'`);
            return;
        }

        const collectionList = document.createElement('ul');

        data.subscribedApps.
        filter(app => app.appName != 'billing').
        filter(app => !app.isOverwritten).
        forEach((subscribedApp:subscribedAppDto) => {
            const li = document.createElement('li');
            li.classList.add('mdc-list-item');
            li.setAttribute('role','option');
            li.dataset.appname = subscribedApp.appName;
            li.dataset.value = subscribedApp.appName;

            const ripple = document.createElement('span');
            ripple.classList.add('mdc-list-item__ripple')

            li.appendChild(ripple);

            li.insertAdjacentHTML('beforeend', `<span class="mdc-list-item__text">${subscribedApp.appName}</span>`);

            collectionList.appendChild(li);
        });

        ul.innerHTML = collectionList.innerHTML;

        this.resetAppList();
    };

    private resetAppList = (): void => {
        if (this.selectObserver) {
            this.selectObserver.disconnect();
        }

        const mdcSelect: mdc.select.MDCSelect = this._mdcSelects['appList_select'];

        mdcSelect.layoutOptions();
        mdcSelect.handleBlur();

        this.setupSelectObserver();
    };

    private renderAppOverwriteList = () => {
        const data = this.readDataFromElement();
        if (!data) {
            console.log('renderAppList: data missing');
            return;
        }

        const ul = document.getElementById(this.appOvListId);
        if (!ul) {
            console.log(`renderAppList: cannot find ul element with id '${this.appListId}'`);
            return;
        }

        const emptyList = document.getElementById('overwriteListEmpty');
        const collectionList = document.createElement('ul');

        const overwritesEmpty = !data.overwrites || data.overwrites.length < 1;
        if (overwritesEmpty) {
            emptyList.classList.remove('hide-element');
        } else {
            if (!emptyList.classList.contains('hide-element')) {
                emptyList.classList.add('hide-element');
            }

            data.overwrites.forEach((ovApps: overwrittenAppDto) => {
                const li = document.createElement('li');
                li.classList.add('mdc-list-item');
                li.dataset.appname = ovApps.appName;

                const ripple = document.createElement('span');
                ripple.classList.add('mdc-list-item__ripple')

                li.appendChild(ripple);

                li.insertAdjacentHTML('beforeend',
                    `<span class="mdc-list-item__text">
                     <span class="mdc-list-item__primary-text">${ovApps.appName}</span>
                     <span class="mdc-list-item__secondary-text">${ovApps.overwriteValue}</span>
                 </span>
                <span aria-hidden="true" class="mdc-list-item__meta">
                <button class="mdc-icon-button material-icons dvcc-deleteOv" data-appname="${ovApps.appName}">delete</button>
</span>`);

                collectionList.appendChild(li);
            });
        }

        ul.innerHTML = collectionList.innerHTML;

        this.resetAppOverwriteList();
    };

    private addDeleteOverwriteListener = () => {
        const deleteButtons = document.querySelectorAll('.dvcc-deleteOv');
        deleteButtons.forEach(btn => {
            btn.removeEventListener('click', this.handleDeleteOverwrite);
            btn.addEventListener('click', this.handleDeleteOverwrite);
        });
    };

    private handleDeleteOverwrite = (e: Event) => {
        const currentButton = <HTMLButtonElement>e.target;
        const appName = currentButton.dataset.appname;

        const dto: requestDto = {
            appName: appName,
            endpoint: ''
        }

        this.removeOverwrite(dto,this.removeData, this.showErrorMessage);
    };

    private resetAppOverwriteList = (): void => {
        const mdcList: mdc.list.MDCList = this._mdcLists[this.appOvListId];
        if (!mdcList) {
            console.log(`resetTenantList: cannot find mdcList with id ${this.appListId}`);
            return
        }

        mdcList.destroy();

        this._mdcLists[this.appListId] = mdc.list.MDCList.attachTo(document.getElementById(this.appOvListId));
        this.addDeleteOverwriteListener();
    };

    private readDataFromElement = (): tenantDto => {
        const el = document.getElementById(this.tenantDataId);
        if(!el) {
            console.error(`cannot find element with id '${this.tenantDataId}'`);
            return;
        }

        const data = el.dataset.tenant;
        if(!data) {
            return null
        }
        return JSON.parse(data);
    };

    private writeDataToElement = (data: tenantDto): void => {
        const el = document.getElementById(this.tenantDataId);
        if(!el) {
            console.error(`cannot find element with id '${this.tenantDataId}'`);
            return;
        }
        el.dataset.tenant = JSON.stringify(data);
    };

    private addData = (dto: requestDto) => {
        const data = this.readDataFromElement();
        data.subscribedApps.forEach(value => {
            if (value.appName === dto.appName) value.isOverwritten = true;
        });

        if (!data.overwrites) {
            data.overwrites = [];
        }

        data.overwrites.push({appName: dto.appName, overwriteValue: dto.endpoint});
        this.writeDataToElement(data);

        this.closeProgress();

        this.clearInputs();
        this.sendTenantUpdatedEvent();
    };

    private removeData = (dto: requestDto) => {
        const data = this.readDataFromElement();

        data.subscribedApps.forEach(value => {
            if (value.appName === dto.appName) value.isOverwritten = false;
        });
        if (data.overwrites.length == 1) {
            data.overwrites = [];
        } else {
            data.overwrites.forEach((item, index) => {
                if (item.appName === dto.appName) data.overwrites.splice(index, 1);
            });
        }

        this.writeDataToElement(data);

        this.closeProgress();
        this.sendTenantUpdatedEvent();
    };

    private showErrorMessage = (msg: string) => {
        const sbError = this._mdcSnackbar[this.errorMessageId];
        sbError.labelText = msg;
        this.closeProgress();
    };

    private addChangedData = (data: requestDto, cbSuccess: (data: requestDto) => void, cbError: (e) => void) => {
        this.openProgress();
        const url = UrlHelpers.getLinkRelHref('self');
        const req = new Request(url,{
            method:'PATCH',
            headers: {
                'content-type':this.contentTypeHeaderValue
            },
            credentials: 'same-origin',
            body: JSON.stringify(data)
        });

        fetch(req)
            .then(response => {
                if (response.ok){
                    cbSuccess(data);
                } else {
                    const msg = this._i18n.getTranslation('tenant_overwrites_msg_error_savefailed');
                    response.text()
                        .then(message => {
                            console.error(`${msg}: ${message} (${response.status})`);
                            cbError(`save changes failed: ${message} (${response.status})`);
                        })
                        .catch(reason => {
                            console.error(`save changes failed, but cannot read body text: ${reason} (${response.status})`);
                            cbError(`${msg} (${response.status})`);
                        });
                }
            })
            .catch(reason => {
                console.error(`cannot save changes: ${reason}`);
                const msg = this._i18n.getTranslation('tenant_overwrites_msg_error_savefailed');
                cbError(msg);
            });
    };

    private removeOverwrite = (data: requestDto, cbSuccess: (data: requestDto) => void, cbError: (e) => void) => {
        this.openProgress();
        const url = UrlHelpers.getLinkRelHref('self');
        const req = new Request(url, {
            method:'PATCH',
            headers: {
                'content-type':this.contentTypeHeaderValue
            },
            credentials: 'same-origin',
            body: JSON.stringify(data)
        });

        fetch(req)
            .then(response => {
                if (response.ok){
                    cbSuccess(data);
                } else {
                    const msg = this._i18n.getTranslation('tenant_overwrites_msg_error_deletefailed');
                    response.text()
                        .then(message => {
                            console.error(`save changes failed: ${message} (${response.status})`);
                            cbError(`${msg}: ${message} (${response.status})`);
                        })
                        .catch(reason => {
                            console.error(`save changes failed, but cannot read body text: ${reason} (${response.status})`);
                            cbError(`${msg} (${response.status}).`);
                        });
                }
            })
            .catch(reason => {
                console.error(`cannot save changes: ${reason}`);
                const msg = this._i18n.getTranslation('tenant_overwrites_msg_error_deletefailed');
                cbError(msg);
            });
    }

    private handleBtnAddClicked = () => {
        const mdcTextField = this._mdcTextFields['overwriteEndpoint_wrapper'];
        if (!mdcTextField) {
            console.error('mdc text field overwriteEndpoint_input missing');
            return
        }

        const selector = document.getElementById('appList_select');
        if(!selector) {
            console.error('selector missing');
            return;
        }

        mdcTextField.valid = true;

        const hostValue = mdcTextField.value;
        if(hostValue.isNullOrEmpty()) {
            console.error('endpoint missing');
            mdcTextField.helperTextContent = this._i18n.getTranslation('tenant_overwrites_msg_error_endpoint_missing');
            return;
        }

        const appName = selector.dataset.selectedapp

        const endpoint = `https://${hostValue}`;
        if (!UrlHelpers.validateURL(endpoint)) {
            console.error('invalid endpoint! => ',endpoint);
            mdcTextField.helperTextContent  = this._i18n.getTranslation('tenant_overwrites_msg_error_endpoint_invalid');
            mdcTextField.valid = false;
            return;
        }

        const requestDto:requestDto = {
            appName: appName,
            endpoint: endpoint,
        }

        this.addChangedData(requestDto,this.addData, this.showErrorMessage);
    };

    private clearInputs() {
        const mdcTextField = this._mdcTextFields['overwriteEndpoint_wrapper'];
        if (!mdcTextField) {
            console.error('mdc text field overwriteEndpoint_input missing');
            return
        }

        const selector = document.getElementById('appList_select');
        if(!selector) {
            console.error('selector missing');
            return;
        }

        mdcTextField.value = '';

        const mdcSelector = this._mdcSelects[selector.id];
        if (!mdcSelector) {
            console.error("mdc selector not found by id ", selector.id);
            return
        }

        selector.dataset.selectedapp = '';
        mdcSelector.selectedIndex = -1;
    }

    private disableBtnAdd = (disabled:boolean = true) => {
        const btn = <HTMLButtonElement>document.getElementById('btnAddOverwrite');
        btn.disabled = disabled;
    };

    private openProgress = () => {
        const id = 'loadListProgress';
        const progressBar = this._mdcProgressBars[id];
        progressBar.open();
    };

    private closeProgress = () => {
        const id = 'loadListProgress';
        const progressBar = this._mdcProgressBars[id];
        progressBar.close();
    };

    private sendTenantUpdatedEvent = () => {
        try {
            this._dapi.dispatchResourceEvent({
                name: "changed",
                uri: tenant_updated
            });
        } catch (e) {
            console.debug("cannot send event to update tenant page: ", e);
        }
    }
}

export interface dto {
    tenant: tenantDto
}

export interface tenantDto {
    tenantId: string
    subscribedApps: subscribedAppDto[]
    overwrites: overwrittenAppDto[]
}

export interface subscribedAppDto {
    appName: string
    isOverwritten: boolean
}

export interface overwrittenAppDto {
    appName: string
    overwriteValue: string
}

export interface requestDto {
    appName: string
    endpoint: string
}